import { directVector } from "./native-vector-display";
import { supportsPenSmoothing } from "../native/pen-smoothing";
import { effects } from "../native/effects";
import { vectorPath } from "../native/vector-path";
import type { NativeFrame } from "./native-port";
import { visibleNodes } from "./native-visible";
import {
  needsDisplayRaster,
  sameRasterLayout,
} from "./native-raster-continuity";
import { activePage } from "../native/pages";
import { isInteractive } from "../native/interactive";
import type { EngineDocument, SceneNode, Point } from "../scene/model";
import type { Box } from "../native/geometry";
import { clippingBase } from "../native/groups";
import type { ImageStore } from "./image-store";
import type { AffineMatrix } from "../geometry/affine";
import { rasterMotion } from "./native-raster-motion";
import { rasterTranslation } from "./native-raster-translation";
import { nodeMatrix } from "../native/transforms";
import { paintRaster, rasterBox } from "./native-raster-paint";
import { renderCanvas, type RenderCanvas } from "./canvas-surface";
export interface RasterEntry {
  node: SceneNode;
  base: SceneNode | undefined;
  density: number;
  /** Display-only mapping from the cached document coordinates to the current pose. */
  transform?: AffineMatrix;
  canvas: RenderCanvas | ImageBitmap;
  box: Box;
  fonts: number;
  assets: EngineDocument["assets"];
}
type RasterRequest = Pick<
  RasterEntry,
  "node" | "base" | "assets" | "fonts" | "density"
>;
function release(surface: RenderCanvas | ImageBitmap) {
  if ("close" in surface) surface.close();
  else surface.width = surface.height = 0;
}
/** Display refinement runs off the UI thread. Hit testing/export retain the exact synchronous path. */
export class NativeRasterCache {
  private entries = new Map<string, RasterEntry>();
  private pending = new Map<string, RasterRequest>();
  private failed = new WeakSet<SceneNode>();
  private fonts = 0;
  private disposed = false;
  private allowance = Infinity;
  private cold = new Map<string, object>();
  private frameStart = Infinity;
  private warming = false;
  private lastDocument: EngineDocument | undefined;
  private beforeDocument: EngineDocument | undefined;
  private releaseEntry(id: string, entry: RasterEntry) {
    const cold = this.cold.get(id);
    if (cold) this.images.displayWork.cancel(cold);
    this.cold.delete(id);
    this.images.pixels.remove(entry.canvas);
    release(entry.canvas);
    if (this.entries.get(id)?.canvas === entry.canvas) this.entries.delete(id);
    this.pending.delete(id);
    if (this.display) this.images.worker.cancelRaster(id);
  }
  private store(id: string, entry: RasterEntry) {
    this.entries.set(id, entry);
    this.images.pixels.add(
      entry.canvas,
      entry.canvas.width * entry.canvas.height * 4,
      this.display ? "displayRasters" : "hitRasters",
      () => this.releaseEntry(id, entry),
    );
  }
  beginFrame(frame: NativeFrame) {
    this.frameStart = performance.now();
    if (this.lastDocument !== frame.document) {
      this.beforeDocument =
        this.lastDocument?.assets === frame.document.assets &&
        this.lastDocument.activePageId === frame.document.activePageId
          ? this.lastDocument
          : undefined;
      this.lastDocument = frame.document;
    }
    const nodes = visibleNodes(frame, needsDisplayRaster);
    this.prune(new Set(nodes.map((n) => n.id)));
    // Equal per-object ceilings prevent a visible scene larger than the cache from
    // endlessly evicting and recreating its own pixels. Export has no such ceiling.
    this.allowance =
      (this.images.pixels.limit * 0.6) / Math.max(1, nodes.length);
    for (const [id, entry] of this.entries)
      if (entry.canvas.width * entry.canvas.height * 4 > this.allowance * 1.01)
        this.releaseEntry(id, entry);
  }
  private fontChanged = () => {
    this.fonts++;
  };
  constructor(
    private images: ImageStore,
    private display = false,
  ) {
    document.fonts.addEventListener("loadingdone", this.fontChanged);
  }
  get(document: EngineDocument, node: SceneNode, density: number): RasterEntry {
    const base = node.layer.clipToBelow
      ? clippingBase(activePage(document).nodes, node)
      : undefined;
    const box = rasterBox(node);
    const requested = 2 ** Math.ceil(Math.log2(Math.max(0.125, density)));
    const ceiling = Math.sqrt(
      this.allowance / (4 * (box.width + 1) * (box.height + 1)),
    );
    const bucket = Math.min(
      requested,
      ceiling === Infinity ? requested : 2 ** Math.floor(Math.log2(ceiling)),
    );
    let previous = this.entries.get(node.id);
    if (previous) this.images.pixels.touch(previous.canvas);
    const pending = this.pending.get(node.id);
    if (
      pending &&
      (pending.node !== node ||
        pending.base !== base ||
        pending.assets !== document.assets ||
        pending.fonts !== this.fonts ||
        pending.density < bucket)
    ) {
      // Also invalidate when Undo returns to an already cached sharp snapshot.
      this.pending.delete(node.id);
      this.images.worker.cancelRaster(node.id);
    }
    const same =
      previous?.node === node &&
      previous.base === base &&
      previous.assets === document.assets &&
      previous.fonts === this.fonts;
    // A sharper raster remains valid when zooming out: never redo pixels just to discard detail.
    if (same && previous && previous.density >= bucket) return previous;
    if (
      previous &&
      previous.base === base &&
      previous.assets === document.assets &&
      previous.density >= bucket &&
      previous.fonts === this.fonts
    ) {
      const delta = rasterTranslation(previous.node, node);
      if (delta && previous.density > 0) {
        const cold = this.cold.get(node.id);
        if (cold) this.images.displayWork.cancel(cold);
        this.cold.delete(node.id);
        previous = {
          ...previous,
          node,
          box: {
            ...previous.box,
            x: previous.box.x + delta.x,
            y: previous.box.y + delta.y,
          },
        };
        this.entries.set(node.id, previous);
        return previous;
      }
    }
    const fx = effects(node);
    const sharpPen =
      this.display &&
      !base &&
      !node.layer.clipToBelow &&
      !node.geometry.warpCorners &&
      !isInteractive(node) &&
      supportsPenSmoothing(node) &&
      vectorPath(node).anchors.length <= 512 &&
      !fx.blur &&
      !fx.shadow &&
      !fx.hue &&
      !fx.saturation &&
      !fx.background.enabled &&
      fx.edge.style === "none" &&
      box.width * box.height * bucket * bucket <= 4_000_000;
    if (
      sharpPen &&
      (previous || this.warming || performance.now() - this.frameStart < 3)
    ) {
      const entry = {
        ...paintRaster(document, node, bucket, this.images),
        node,
        base,
        density: bucket,
        fonts: this.fonts,
        assets: document.assets,
      };
      if (previous) this.releaseEntry(node.id, previous);
      this.store(node.id, entry);
      return entry;
    }
    const asynchronous =
      this.display &&
      this.images.worker.available() &&
      !this.failed.has(node) &&
      [node, base].every((n) => !n || (n.kind !== "text" && !isInteractive(n)));
    const motion =
      this.display &&
      previous &&
      previous.density > 0 &&
      previous.assets === document.assets &&
      previous.fonts === this.fonts &&
      !sameRasterLayout(previous.node, node)
        ? rasterMotion(previous.node, node, previous.base, base)
        : null;
    const beforeDocument = this.beforeDocument;
    const before =
      !previous &&
      asynchronous &&
      beforeDocument?.assets === document.assets &&
      beforeDocument.activePageId === document.activePageId
        ? activePage(beforeDocument).nodes.find((n) => n.id === node.id)
        : undefined;
    const firstEffect =
      before &&
      (!needsDisplayRaster(before) || directVector(before)) &&
      !base &&
      sameRasterLayout(before, node);
    const retainSharp =
      asynchronous &&
      previous &&
      previous.density > 0 &&
      previous.assets === document.assets &&
      previous.fonts === this.fonts &&
      sameRasterLayout(previous.node, node) &&
      sameRasterLayout(previous.base, base);
    if (
      this.display &&
      !this.warming &&
      !retainSharp &&
      !motion &&
      !firstEffect &&
      performance.now() - this.frameStart >= 3
    ) {
      if (same && previous && this.cold.has(node.id)) return previous;
      if (previous && !same) {
        this.releaseEntry(node.id, previous);
        previous = undefined;
      }
      if (!previous) {
        const canvas = renderCanvas();
        canvas.width = canvas.height = 1;
        previous = {
          canvas,
          box,
          node,
          base,
          density: 0,
          fonts: this.fonts,
          assets: document.assets,
        };
        this.store(node.id, previous);
      }
      const ticket = {},
        entry = previous;
      const old = this.cold.get(node.id);
      if (old) this.images.displayWork.cancel(old);
      this.cold.set(node.id, ticket);
      this.images.displayWork.enqueue(ticket, () => {
        if (
          this.disposed ||
          this.cold.get(node.id) !== ticket ||
          this.entries.get(node.id) !== entry
        )
          return;
        this.cold.delete(node.id);
        this.warming = true;
        try {
          this.get(document, node, density);
        } finally {
          this.warming = false;
          this.images.invalidate();
        }
      });
      return previous;
    }
    if (asynchronous) {
      const cold = this.cold.get(node.id);
      if (cold) this.images.displayWork.cancel(cold);
      this.cold.delete(node.id);
      // Turning on the first effect must not blur a shape/image that was drawn directly.
      // Repaint only its inexpensive previous appearance; expensive effects stay in the worker.
      if (firstEffect) {
        const m = nodeMatrix(before);
        previous = {
          ...paintRaster(beforeDocument!, before, bucket, {
            get: (source) =>
              this.images.display(
                source,
                Math.hypot(m[0], m[1]) * density,
                Math.hypot(m[2], m[3]) * density,
              ),
          }),
          node: before,
          base: undefined,
          density: bucket,
          fonts: this.fonts,
          assets: document.assets,
        };
        this.store(node.id, previous);
      } else if (!same && !retainSharp && !motion) {
        const b = rasterBox(node),
          coarse = Math.min(
            bucket,
            0.5,
            Math.sqrt(65536 / (b.width * b.height)),
          );
        const painted = paintRaster(document, node, coarse, this.images);
        if (previous) this.releaseEntry(node.id, previous);
        previous = {
          ...painted,
          node,
          base,
          density: coarse,
          fonts: this.fonts,
          assets: document.assets,
        };
        this.store(node.id, previous);
        this.pending.delete(node.id);
      }
      const current = previous!;
      if (!this.pending.has(node.id)) {
        const ticket: RasterRequest = {
          node,
          base,
          assets: document.assets,
          fonts: this.fonts,
          density: bucket,
        };
        this.pending.set(node.id, ticket);
        void this.images.worker
          .raster(document, node, base, bucket, (s) => this.images.get(s))
          .then((result) => {
            if (
              this.disposed ||
              this.pending.get(node.id) !== ticket ||
              this.entries.get(node.id) !== current ||
              current.fonts !== this.fonts
            ) {
              result.image?.close();
              return;
            }
            this.images.pixels.remove(current.canvas);
            release(current.canvas);
            this.store(node.id, {
              ...ticket,
              canvas: result.image!,
              box: result.box!,
              density: bucket,
            });
          })
          .catch(() => {
            if (
              this.pending.get(node.id) === ticket &&
              this.images.worker.available()
            )
              this.failed.add(node);
          })
          .finally(() => {
            if (this.pending.get(node.id) === ticket)
              this.pending.delete(node.id);
            if (!this.disposed) this.images.invalidate();
          });
      }
      return motion ? { ...current, transform: motion } : current;
    }
    this.pending.delete(node.id);
    const entry = {
      ...paintRaster(document, node, bucket, this.images),
      node,
      base,
      density: bucket,
      fonts: this.fonts,
      assets: document.assets,
    };
    if (previous) this.releaseEntry(node.id, previous);
    this.store(node.id, entry);
    return entry;
  }
  prune(ids: Set<string>) {
    for (const [id, entry] of this.entries)
      if (!ids.has(id)) {
        this.releaseEntry(id, entry);
      }
  }
  hit(document: EngineDocument, node: SceneNode, p: Point) {
    const { canvas, box } = this.get(document, node, 1),
      x = Math.floor(((p.x - box.x) * canvas.width) / box.width),
      y = Math.floor(((p.y - box.y) * canvas.height) / box.height);
    if (x < 0 || y < 0 || x >= canvas.width || y >= canvas.height) return false;
    if ("getContext" in canvas) {
      const hit =
        canvas.getContext("2d")!.getImageData(x, y, 1, 1).data[3]! > 0;
      this.images.pixels.trim();
      return hit;
    }
    const sample = renderCanvas();
    sample.width = sample.height = 1;
    const ctx = sample.getContext("2d")!;
    ctx.drawImage(canvas, x, y, 1, 1, 0, 0, 1, 1);
    return ctx.getImageData(0, 0, 1, 1).data[3]! > 0;
  }
  dispose() {
    this.disposed = true;
    document.fonts.removeEventListener("loadingdone", this.fontChanged);
    this.prune(new Set());
    this.lastDocument = this.beforeDocument = undefined;
  }
}
